<script setup lang="ts">
import { computed } from 'vue';

interface Props {
  isLoading?: boolean;
  isTag?: boolean;
  label: string;
  placeholder?: string;
  tagColor?: 'danger' | 'info' | 'primary' | 'success' | 'warning';
  value?: null | number | string;
}

const props = withDefaults(defineProps<Props>(), {
  isLoading: false,
  isTag: false,
  placeholder: '—',
  tagColor: 'primary',
  value: undefined,
});

const displayValue = computed(() => {
  if (props.value === null || props.value === undefined || props.value === '') {
    return props.placeholder;
  }

  return props.value;
});

const tagClass = computed(() => `badge bg-${props.tagColor}-lt`);
const isEmpty = computed(() => props.value === null || props.value === undefined || props.value === '');
</script>

<template>
  <div class="datagrid-item" :aria-label="label">
    <div class="datagrid-content">
      <span v-if="props.isLoading" class="skeleton skeleton-value" />
      <slot v-else-if="$slots.default" />
      <span v-else-if="isTag" :class="tagClass">{{ displayValue }}</span>
      <span v-else :class="{ 'value-empty': isEmpty }">{{ displayValue }}</span>
    </div>
    <div class="datagrid-title">
      <span v-if="props.isLoading" class="skeleton skeleton-title" />
      <span v-else class="title-text">{{ label }}</span>
    </div>
  </div>
</template>

<style scoped lang="scss">
.datagrid-item {
  display: flex;
  flex-direction: column;
  min-height: 3rem;
  transition: opacity 0.2s ease;
}

.datagrid-content {
  display: flex;
  align-items: center;
  font-size: 1rem;
  font-weight: 600;
  color: #0f172a;
  margin-bottom: 0.375rem;
  line-height: 1.5;
  min-height: 1.5rem;

  .value-empty {
    color: #94a3b8;
    font-weight: 400;
    font-style: italic;
  }
}

.datagrid-title {
  .title-text {
    font-family: 'Lato', sans-serif;
    font-size: 0.6875rem;
    font-weight: 600;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: #64748b;
    line-height: 1.4;
  }
}

.skeleton {
  display: inline-block;
  border-radius: 0.375rem;
  background: linear-gradient(90deg, #e2e8f0 0%, #f1f5f9 50%, #e2e8f0 100%);
  background-size: 200% 100%;
  animation: skeleton-loading 1.5s ease-in-out infinite;
}

.skeleton-title {
  width: 60%;
  height: 0.6875rem;
  border-radius: 0.25rem;
}

.skeleton-value {
  width: 100%;
  height: 1.25rem;
  border-radius: 0.375rem;
}

@keyframes skeleton-loading {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}
</style>
